const { ethers } = require("ethers");

// Calculates the scale adjustment between two tokens
//
// * payoutTokenDecimals: BigNumberish - Number of decimals defined for the token in its ERC20 contract
// * payoutPriceDecimal: BigNumberish - The significand of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 3)
// * quoteTokenDecimals: BigNumberish - Number of decimals defined for the token in its ERC20 contract
// * quotePriceDecimal: BigNumberish - The significand of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 3)
// final formula: payoutTokenDecimals - quoteTokenDecimals - (payoutPriceDecimal - quotePriceDecimal) / 2
function calculateScaleAdjustment(payoutTokenDecimals, payoutPriceDecimal, quoteTokenDecimals, quotePriceDecimal) {
  return payoutTokenDecimals.sub(quoteTokenDecimals).sub(payoutPriceDecimal.sub(quotePriceDecimal).div(2));
}

// Calculates the formatted price between two tokens
// price is formatted as payoutToken/quoteToken * 10^36
//
// * payoutTokenDecimals: BigNumberish - Number of decimals defined for the token in its ERC20 contract
// * payoutPriceCoefficient: FixedNumber-ish - The coefficient of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 1.5)
// * payoutPriceDecimal: BigNumberish - The significand of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 3)
// * quoteTokenDecimals: BigNumberish - Number of decimals defined for the token in its ERC20 contract
// * quotePriceCoefficient: FixedNumber-ish - The coefficient of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 1.5)
// * quotePriceDecimal: BigNumberish - The significand of the token price in scientific notation (e.g. if $1,500 -> 1.5 * 10^3,
//    so in this case parameter should be equal to 3)
// * scaleAdjustment: BigNumberish - The scale adjustment calculated by the function `calculateScaleAdjustment`
// $power = scaleAdjustment + 36 + (quoteTokenDecimals - quotePriceDecimal) - (payoutTokenDecimals - payoutPriceDecimal)
// final formula: 10^$power * payoutPriceCoefficient / quotePriceCoefficient
function calculateFormatedPrice(
  payoutTokenDecimals,
  payoutPriceCoefficient,
  payoutPriceDecimal,
  quoteTokenDecimals,
  quotePriceCoefficient,
  quotePriceDecimal,
  scaleAdjustment
) {
  const power = scaleAdjustment
    .add(36)
    .add(quoteTokenDecimals)
    .sub(payoutTokenDecimals)
    .add(payoutPriceDecimal)
    .sub(quotePriceDecimal);
  return ethers.BigNumber.from(10).pow(power).mul(payoutPriceCoefficient).div(quotePriceCoefficient);
}

// Convert price to coefficient and decimal
//
// Example: 0.00076 -> {priceCoefficient: 7.6, priceDecimals: -4}
// Example: 1500 -> {priceCoefficient: 1.5, priceDecimals: 3}
function preparePrice(price) {
  let priceCoefficient = price;
  let priceDecimals = 0;

  if (price < 1) {
    while (priceCoefficient < 1) {
      priceDecimals -= 1;
      priceCoefficient *= 10;
    }
  } else {
    while (priceCoefficient > 9) {
      priceDecimals += 1;
      priceCoefficient /= 10;
    }
  }

  return {
    priceCoefficient: ethers.FixedNumber.from(`${priceCoefficient}`),
    priceDecimals: ethers.BigNumber.from(priceDecimals),
  };
}

module.exports = {
  calculateScaleAdjustment,
  calculateFormatedPrice,
  preparePrice,
};
